function getLocationSearchKey(item) {
    return String(item.id ?? item.cityID ?? '');
}
function mergeRecentSearchesById(accountRecents, persistedRecents, maxCount) {
    const seen = new Set();
    const out = [];
    for (const source of [
        accountRecents,
        persistedRecents
    ])for (const item of source){
        const key = getLocationSearchKey(item);
        if (!('' === key || seen.has(key))) {
            seen.add(key);
            out.push(item);
            if (out.length >= maxCount) return out;
        }
    }
    return out;
}
function excludeNeighbourhoodRecentSearches(recentSearches) {
    return recentSearches.filter((item)=>'NEIGHBOURHOOD' !== item.type);
}
function dedupeRecommendationsAgainstRecents(recentSearches, recommendedSearches) {
    const recentKeys = new Set(recentSearches.map(getLocationSearchKey).filter((key)=>'' !== key));
    return recommendedSearches.filter((item)=>{
        const key = getLocationSearchKey(item);
        return '' === key || !recentKeys.has(key);
    });
}
function resolveClickListTypeForItem(selectedItem, listType, recentSearches, recommendedSearches) {
    if ('recent_and_recommended' !== listType) return listType;
    const selectedKey = getLocationSearchKey(selectedItem);
    if ('' === selectedKey) return listType;
    if (recentSearches.some((item)=>getLocationSearchKey(item) === selectedKey)) return 'recent_searches';
    const recommendationsExcludingRecents = dedupeRecommendationsAgainstRecents(recentSearches, recommendedSearches);
    if (recommendationsExcludingRecents.some((item)=>getLocationSearchKey(item) === selectedKey)) return 'recommended';
    return listType;
}
function isEmptyAutocompleteInput(value) {
    return null === value || '' === value;
}
function hasRecentSearches(recentSearches) {
    return recentSearches.length > 0;
}
function hasRecommendedSearches(recommendedSearches) {
    return recommendedSearches.length > 0;
}
function getEmptyInputListType(hasRecent, hasRecommended) {
    if (hasRecent && hasRecommended) return 'recent_and_recommended';
    if (hasRecommended) return 'recommended';
    return 'recent_searches';
}
function withoutNestingMetadata(item) {
    const rest = {
        ...item
    };
    delete rest.isIndented;
    delete rest.parentId;
    return rest;
}
function asRecentSearchDisplayItems(recentSearches) {
    return recentSearches.map(withoutNestingMetadata);
}
function buildMergedRecentAndRecommendedRows(recentSearches, recommendedSearches) {
    const staticRows = [];
    if (hasRecentSearches(recentSearches)) staticRows.push({
        isGroupHeader: true,
        itemName: 'Recent Searches'
    }, ...asRecentSearchDisplayItems(recentSearches));
    if (hasRecommendedSearches(recommendedSearches)) staticRows.push({
        isGroupHeader: true,
        itemName: 'Recommended Searches'
    }, ...recommendedSearches);
    return staticRows;
}
function buildMergedRecentAndRecommendedResult(recentSearches, recommendedSearches) {
    const recommendationsExcludingRecents = dedupeRecommendationsAgainstRecents(recentSearches, recommendedSearches);
    const staticRows = buildMergedRecentAndRecommendedRows(recentSearches, recommendationsExcludingRecents);
    if (0 === staticRows.length) return null;
    return {
        autocompleteData: staticRows,
        listType: getEmptyInputListType(hasRecentSearches(recentSearches), hasRecommendedSearches(recommendationsExcludingRecents))
    };
}
function buildControlRecentOrRecommendedResult(recentSearches, recommendedSearches) {
    const showRecommended = hasRecommendedSearches(recommendedSearches);
    const itemsToShow = showRecommended ? recommendedSearches : asRecentSearchDisplayItems(recentSearches);
    if (0 === itemsToShow.length) return null;
    return {
        autocompleteData: [
            {
                isGroupHeader: true,
                itemName: showRecommended ? 'Recommended Searches' : 'Recent Searches'
            },
            ...itemsToShow
        ],
        listType: showRecommended ? 'recommended' : 'recent_searches'
    };
}
function buildRecentsOnlyResult(recentSearches) {
    const items = asRecentSearchDisplayItems(recentSearches);
    if (0 === items.length) return null;
    return {
        autocompleteData: [
            {
                isGroupHeader: true,
                itemName: 'Recent Searches'
            },
            ...items
        ],
        listType: 'recent_searches'
    };
}
function buildEmptyInputAutocompleteResult({ mergeRecentAndRecommendedSearches, preferRecentSearchesOnly, recentSearches, recommendedSearches }) {
    if (mergeRecentAndRecommendedSearches) return buildMergedRecentAndRecommendedResult(recentSearches, recommendedSearches);
    if (preferRecentSearchesOnly) return buildRecentsOnlyResult(recentSearches);
    return buildControlRecentOrRecommendedResult(recentSearches, recommendedSearches);
}
function buildAutocompleteDataAndListType({ autocompleteValue, mergeRecentAndRecommendedSearches, preferRecentSearchesOnly, recentSearches, recommendedSearches, typeAheadItems }) {
    if (isEmptyAutocompleteInput(autocompleteValue)) {
        const emptyInputResult = buildEmptyInputAutocompleteResult({
            mergeRecentAndRecommendedSearches,
            preferRecentSearchesOnly,
            recentSearches,
            recommendedSearches
        });
        if (emptyInputResult) return emptyInputResult;
    }
    if (typeAheadItems.length > 0) return {
        autocompleteData: typeAheadItems,
        listType: 'typein'
    };
    return {
        autocompleteData: [],
        listType: void 0
    };
}
function getSelectedChildPosition({ items, selectedId, parentId }) {
    const siblings = items.filter((item)=>item.parentId === parentId);
    const index = siblings.findIndex((item)=>item.id === selectedId);
    return -1 === index ? null : index;
}
function resolveSelectedChildPosition({ items, selectedId, parentId, isIndented, typeAheadEndpoint, neighbourhoodNestingEnabled }) {
    const supportsChildPosition = 'flights' === typeAheadEndpoint || 'hotelsV2' === typeAheadEndpoint && neighbourhoodNestingEnabled;
    if (!isIndented || !parentId || !supportsChildPosition) return null;
    return getSelectedChildPosition({
        items,
        selectedId,
        parentId
    });
}
export { buildAutocompleteDataAndListType, buildEmptyInputAutocompleteResult, dedupeRecommendationsAgainstRecents, excludeNeighbourhoodRecentSearches, getEmptyInputListType, getLocationSearchKey, getSelectedChildPosition, hasRecentSearches, hasRecommendedSearches, isEmptyAutocompleteInput, mergeRecentSearchesById, resolveClickListTypeForItem, resolveSelectedChildPosition };
